import { OverlayRef } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  signal
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { MODAL_CONFIG, ModalRef } from './modal.service';
import { ButtonComponent } from '../forms/button/button.component';
import { modalBodyAnimation, modalBackdropAnimation } from '../animations/index';

export class TemplateModalOverlayRef implements ModalRef<unknown> {
  public id: string;
  public componentInstance?: unknown;

  private readonly _afterClosed = new Subject<unknown>();
  private readonly _afterOpened = new Subject<void>();
  private readonly _isClosing = signal(false);
  readonly isClosing = this._isClosing.asReadonly();
  private _closeResult: unknown;
  private _disposed = false;

  constructor(
    private overlayRef: OverlayRef,
    id: string
  ) {
    this.id = id;
    setTimeout(() => this._afterOpened.next());
  }

  close(result?: unknown): void {
    if (this._disposed || this._isClosing()) return;
    this._closeResult = result;
    this._isClosing.set(true);
    // Safety fallback: dispose if animation done callback doesn't fire
    setTimeout(() => this.finishClose(), 300);
  }

  finishClose(): void {
    if (this._disposed) return;
    this._disposed = true;
    this._afterClosed.next(this._closeResult);
    this._afterClosed.complete();
    this._afterOpened.complete();
    this.overlayRef.dispose();
  }

  afterClosed(): Observable<unknown> {
    return this._afterClosed.asObservable();
  }

  afterOpened(): Observable<void> {
    return this._afterOpened.asObservable();
  }
}

@Component({
    selector: 'app-modal',
    imports: [ButtonComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './modal.component.html',
    styleUrl: './modal.component.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
      '(document:keydown.escape)': 'onEscapeKey()'
    },
    animations: [
        modalBodyAnimation(),
        modalBackdropAnimation()
    ]
})
export class ModalComponent {
  private readonly overlayRef = inject(TemplateModalOverlayRef, { optional: true });
  private readonly modalConfig = inject(MODAL_CONFIG, { optional: true });

  showCloseButton = computed(() => !this.modalConfig?.hideCloseButton);
  animState = computed(() => this.overlayRef?.isClosing() ? 'closing' : 'open');

  private get disableClose(): boolean {
    return this.modalConfig?.disableClose ?? false;
  }

  onBackdropClick(event: MouseEvent): void {
    if (!this.disableClose && event.target === event.currentTarget) {
      this.close();
    }
  }

  onEscapeKey(): void {
    if (!this.disableClose) {
      this.close();
    }
  }

  close(): void {
    this.overlayRef?.close();
  }

  onBodyAnimationDone(event: { toState: string }): void {
    if (event.toState === 'closing') {
      this.overlayRef?.finishClose();
    }
  }
}
